import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { apiPrefix } from 'constants/apiConfig';

import { useActualIncidentId } from './commonHooks';
import { icsFormInputsKeys } from './icsFormInputsHooks';

export type Ics221Checkoff = {
  checked: boolean;
  name?: string;
  remarks?: string;
  position?: string;
  signed_at?: string;
  user_guid?: string;
  datetime?: string;
};

/**
 * The ICS 221 boxes a documentation entry fills; mirrors COMS
 * `Ics221EntryFields`.
 */
export type Ics221EntryFields = {
  resourcePersonnelReleased?: string;
  orderRequestNumber?: string;
  plannedReleaseDatetime?: string | null;
  estimatedDepartureDatetime?: string | null;
  actualReleaseDatetime?: string | null;
  estimatedArrivalDatetime?: string | null;
  destination?: string;
  travelMethod?: string;
  contactWhileTraveling?: string;
  remarks?: string;
  overnightRequired?: boolean;
  manifestRequired?: boolean;
  manifestNumber?: string;
  unitAgencyNotified?: boolean;
  unitAgencyNotifiedName?: string;
  checkoffs?: Record<string, Ics221Checkoff>;
};

/**
 * A change to some boxes, merged server-side into what the sheet holds. A null
 * check-off removes that Block 6 row.
 */
export type Ics221EntryFieldsPatch = Omit<Ics221EntryFields, 'checkoffs'> & {
  checkoffs?: Record<string, Ics221Checkoff | null>;
};

export type Ics221Entry = {
  id: string;
  incidentId: string;
  resourceInIncidentId: string | null;
  resourceLabel: string;
  resourceKind: string;
  /**
   * `checkout` entries hold the edits made on a check-out's sheet; `manual`
   * ones are documentation entries written by hand.
   */
  source: 'manual' | 'checkout';
  fields: Ics221EntryFields;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type Ics221EntryCreate = {
  resourceInIncidentId?: string | null;
  resourceLabel?: string;
  resourceKind?: string;
  fields?: Ics221EntryFields;
};

export type Ics221EntryUpdate = {
  resourceInIncidentId?: string | null;
  resourceLabel?: string;
  resourceKind?: string;
  fields?: Ics221EntryFieldsPatch;
};

export const ics221EntryKeys = {
  all: ['ics221Entries'] as const,
  byIncident: (incidentId: string | undefined) =>
    [...ics221EntryKeys.all, incidentId] as const,
};

const entriesUrl = (incidentId: string) =>
  `${apiPrefix}/dice/ics/${incidentId}/ics221-entries`;

export function useIcs221Entries() {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: ics221EntryKeys.byIncident(incidentId),
    enabled: Boolean(incidentId),
    queryFn: async () => {
      const { data } = await axios.get<{ entries: Ics221Entry[] }>(
        entriesUrl(incidentId)
      );
      return data.entries;
    },
  });
}

function useInvalidateEntries() {
  const queryClient = useQueryClient();
  const incidentId = useActualIncidentId();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ics221EntryKeys.byIncident(incidentId),
      }),
      queryClient.invalidateQueries({ queryKey: icsFormInputsKeys.all }),
    ]);
}

export function useCreateIcs221Entry() {
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateEntries();
  return useMutation({
    mutationFn: async (body: Ics221EntryCreate = {}) => {
      const { data } = await axios.post<Ics221Entry>(
        entriesUrl(incidentId),
        body
      );
      return data;
    },
    // Listed at once, so the new sheet can open before the list refetches.
    onSuccess: (entry) => {
      queryClient.setQueryData<Ics221Entry[]>(
        ics221EntryKeys.byIncident(incidentId),
        (current = []) => [entry, ...current]
      );
      return invalidate();
    },
  });
}

export function useUpdateIcs221Entry() {
  const incidentId = useActualIncidentId();
  const invalidate = useInvalidateEntries();
  return useMutation({
    mutationFn: async ({ id, ...body }: Ics221EntryUpdate & { id: string }) => {
      const { data } = await axios.patch<Ics221Entry>(
        `${entriesUrl(incidentId)}/${id}`,
        body
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

/**
 * Saves boxes on a check-out's sheet. The first save creates the overlay the
 * sheet's edits live in; later ones merge into it.
 */
export function useUpsertIcs221CheckoutOverlay() {
  const incidentId = useActualIncidentId();
  const invalidate = useInvalidateEntries();
  return useMutation({
    mutationFn: async ({
      resourceId,
      fields,
    }: {
      resourceId: string;
      fields: Ics221EntryFieldsPatch;
    }) => {
      const { data } = await axios.put<Ics221Entry>(
        `${entriesUrl(incidentId)}/checkout/${resourceId}`,
        { fields }
      );
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useArchiveIcs221Entry() {
  const incidentId = useActualIncidentId();
  const invalidate = useInvalidateEntries();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${entriesUrl(incidentId)}/${id}`);
    },
    onSuccess: invalidate,
  });
}
